import primitiveFactory from "_src/engine/factories/primitive";
import objFactory from "_src/engine/factories/object";
import {
  pixelBufferType,
  primitiveType
} from "_src/engine/constants";

import {vec3} from "_src/libs/gl-matrix";
import {screenUtils} from "_src/client/utils";
import LineShader from "_src/shaders/line/line";
import DefaultShader from "_src/shaders/default/default_shader";
import output from "_src/engine/output";
import loader from "_src/loader";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import selector from "_src/manipulators/libs/selector";

import dotpointLine from "_assets/textures/dotpoint_line.png";
import {lineColorType} from "_src/constants";

const vec3Tmp = vec3.create();
const vec3Tmp2 = vec3.create();

const BASE_ELEMENTS_COLOR = [1.0, 1.0, 1.0];
const BASE_OPACITY = 0.5;
const SELECTED_OPACITY = 1.0;
const BASE_SPHERE_SIZE = 10;
const SELECTED_SPHERE_SIZE = 20;
const AXIS_WIDTH = 5;
const SEGMENT_LENGTH = 50;

let _lineTexture = null;

export async function initCadAxis() {
  if (_lineTexture) return;

  const image = await loader.load(dotpointLine, loader.IMAGE);
  if (!image) {
    output.error(output.DEVELOPERS_ERROR, "Couldn't process the cad axis texture.");
    return;
  }

  _lineTexture = bufferFactory.create(pixelBufferType.T_RGBA, {width: image.width, height: image.height, immutable: true});
  _lineTexture.initMipMapLevelImage(0, image);
}

export function clearCadAxis() {
  _lineTexture = null;
}

export default class CADAxis {
  constructor() {
    this._basePoints = null;
    this._selectedBasePoint = null;
    this._axis = null;

    this._opaqueScene = null;
    this._transparentScene = null;
  }

  setScenes(opaque, transparent) {
    this._opaqueScene = opaque;
    this._transparentScene = transparent;
  }

  init(modelData) {
    const pointShader = new DefaultShader({
      transparent: true,
      backFaceCulling: true,
      lighting: false,
      writeObjectID: false,
      depthTest: false,
    });
    this._basePoints = objFactory.createDrawable(pointShader);
    this._basePoints.hide();

    const sphereGeometry = primitiveFactory.create(primitiveType.SPHERE);
    for (const spherePosition of modelData["points"] || []) {
      const sphereModel = objFactory.createDrawable(pointShader);
      sphereModel.addGeometryChunk(sphereGeometry);
      sphereModel.setDiffuseColor(...BASE_ELEMENTS_COLOR);
      sphereModel.setOpacity(SELECTED_OPACITY);
      sphereModel.setTranslation(...spherePosition);
      sphereModel.hide();

      this._basePoints.add(sphereModel);
    }

    if (modelData["axis"]) {
      const lineShader = new LineShader({
        lineWidth : AXIS_WIDTH,
        transparent : false,
        colorType: lineColorType.TEXTURE,
        depthTest: false,
        backFaceCulling: false,
        texPeriod: 0.05,
      });
      this._axis = objFactory.createDrawable(lineShader);
      this._axis.addGeometryChunk(primitiveFactory.create(primitiveType.LINE, {x0: modelData["axis"]["start"][0], y0: modelData["axis"]["start"][1],
        z0: modelData["axis"]["start"][2], x1: modelData["axis"]["end"][0], y1: modelData["axis"]["end"][1], z1: modelData["axis"]["end"][2]}));
      this._axis.chunks[0].enableFrustumTest = false;
      this._axis.setTexture(_lineTexture, 0);
      this._axis.setOpacity(BASE_OPACITY);
    }
  }

  syncTransforms(matrix) {
    if (this._basePoints) this._basePoints.setMatrix(matrix);
    if (this._axis) this._axis.setMatrix(matrix);
  }

  addToScenes() {
    //axis and _basePoints order does matter
    if (this._basePoints && this._opaqueScene) this._opaqueScene.add(this._basePoints);
    if (this._axis && this._transparentScene) this._transparentScene.add(this._axis);
  }

  removeFromScenes() {
    if (this._opaqueScene) this._opaqueScene.remove(this._axis);
    if (this._transparentScene) this._transparentScene.remove(this._axis);
    if (this._opaqueScene) this._opaqueScene.remove(this._basePoints);
  }

  updateScreenAppearance() {
    const scene = this._transparentScene || this._opaqueScene;
    if (!scene) return;
    const camera = scene.camera;

    if (this._basePoints) updateBasePointsSize(this._basePoints, this._selectedBasePoint, camera);
    if (this._axis) updateAxisSegments(this._axis, camera);
  }

  setSelected(selected) {
    const addScene = selected ? this._opaqueScene : this._transparentScene;
    const removeScene = selected ? this._transparentScene : this._opaqueScene;

    const opacity = selected ? SELECTED_OPACITY : BASE_OPACITY;

    //axis and _basePoints order does matter
    if (this._basePoints) {
      const basePointsModels = this._basePoints.getChildren();
      this._basePoints.setVisibility(selected);
      for (const model of basePointsModels) {
        model.setVisibility(selected);
      }
    }
    if (this._axis) {
      this._axis.setOpacity(opacity);
      addScene.add(this._axis);
      removeScene.remove(this._axis);
    }
  }

  setVisibility(visible) {
    if (this._axis) this._axis.setVisibility(visible);
  }

  pickPoint(screenX, screenY) {
    let coords = null;
    if (this._basePoints && this._opaqueScene) {
      const camera = this._opaqueScene.camera;

      const basePointsModels = this._basePoints.getChildren();
      this._selectedBasePoint = basePointsModels[0];

      if (basePointsModels.length === 3) {
        const {x, y} = screenUtils.toScreen(camera.viewport, camera.viewport, screenX, screenY);

        const intersectedObject = selector.findClosestIntersection(null, x, y, camera, basePointsModels);
        if (intersectedObject) {
          this._selectedBasePoint = intersectedObject;
        }
      }

      updateBasePointsSize(this._basePoints, this._selectedBasePoint, camera);
      coords = vec3.transformMat4(vec3.create(), this._selectedBasePoint.trans, this._basePoints.modelMatrix);
    }

    return coords;
  }
}

function updateBasePointsSize(basePoints, selectedPoint, camera) {
  for (const sphereModel of basePoints.getChildren()) {
    const size = sphereModel === selectedPoint ? SELECTED_SPHERE_SIZE : BASE_SPHERE_SIZE;
    const scale = screenUtils.calculateObjectScale(size, 2, sphereModel.trans, camera);
    sphereModel.setScale(scale, scale, scale);
  }
}

function updateAxisSegments(axis, camera) {
  const posBuffer = axis.chunks[0].geometry.getItemBuffer("a_position");
  const posNextBuffer = axis.chunks[0].geometry.getItemBuffer("a_position_next");
  const pos1 = vec3.set(vec3Tmp, posBuffer[0], posBuffer[1], posBuffer[2]);
  const pos2 = vec3.set(vec3Tmp2, posNextBuffer[0], posNextBuffer[1], posNextBuffer[2]);
  const texturePeriod = screenUtils.calculateSegmentLinePeriod(pos1, pos2, SEGMENT_LENGTH, camera);
  axis.shader.setTexturePeriod(texturePeriod);
}
